import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Flower2, ArrowRight, Lock } from "lucide-react";
import Navbar from "@/components/Navbar";
import QuoteCheckout from "@/components/QuoteCheckout";
import { api } from "@/lib/api";

export default function SharedBouquet() {
  const { shareId } = useParams();
  const [data, setData] = useState(undefined);
  const [config, setConfig] = useState(null);
  const [showQuote, setShowQuote] = useState(false);
  const [unlockedPrice, setUnlockedPrice] = useState(null);

  useEffect(() => {
    api.get(`/share/${shareId}`).then((r) => setData(r.data)).catch(() => setData(null));
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, [shareId]);

  if (data === undefined) {
    return (
      <div className="min-h-screen"><Navbar />
        <div className="flex justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      </div>
    );
  }

  if (data === null) {
    return (
      <div className="min-h-screen"><Navbar />
        <div className="text-center py-32">
          <Flower2 className="w-10 h-10 text-primary mx-auto mb-4" />
          <p className="text-lg">This bouquet could not be found.</p>
          <Link to="/studio" className="inline-block mt-6 rounded-full px-6 py-3 font-medium bg-primary text-primary-foreground hover:bg-[#2C4631] transition-all">Design your own</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-12 grid md:grid-cols-2 gap-10 items-start">
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}>
          <div className="aspect-square rounded-[2rem] overflow-hidden border border-border shadow-lg">
            <img src={data.image} alt={data.name} className="w-full h-full object-cover" data-testid="shared-image" />
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-secondary">Shared by {data.creator_name}</span>
          <h1 className="font-serif-display text-4xl sm:text-5xl mt-3">{data.name}</h1>

          {unlockedPrice ? (
            <div className="mt-8 bg-white rounded-2xl border border-border p-6" data-testid="shared-price-panel">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Bouquet price</span>
                <span className="font-serif-display text-3xl text-primary" data-testid="shared-total">${unlockedPrice.total.toFixed(2)}</span>
              </div>
              <div className="space-y-1">
                {unlockedPrice.lines.map((l, i) => (
                  <div key={i} className="flex justify-between text-sm text-muted-foreground">
                    <span>{l.label}</span><span>${l.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-8 bg-white rounded-2xl border border-border p-6" data-testid="shared-price-locked">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Florist quote</span>
                <span className="font-serif-display text-3xl text-muted-foreground/60 flex items-center gap-1.5">
                  <Lock className="w-5 h-5" /> $•••
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Unlock this bouquet's full price &amp; an obligation-free quote from {config?.florist_name || "our florist"} — no commitment.
              </p>
              <button
                onClick={() => setShowQuote(true)}
                data-testid="shared-get-quote-btn"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-medium bg-secondary text-white hover:bg-[#C29363] transition-all shadow-sm"
              >
                <Lock className="w-4 h-4" />
                Unlock price{config?.quote_fee ? ` — $${config.quote_fee.toFixed(2)}` : ""}
              </button>
            </div>
          )}

          <Link to="/studio" data-testid="design-own-btn" className="group mt-4 inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-medium bg-primary text-primary-foreground hover:bg-[#2C4631] transition-all shadow-sm">
            Design your own bouquet
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>

      <QuoteCheckout
        open={showQuote}
        onOpenChange={setShowQuote}
        config={config}
        bouquetName={data.name}
        shareId={shareId}
        spec={data.spec}
        onPaid={(p) => setUnlockedPrice(p)}
      />
    </div>
  );
}
